<script lang="ts" setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useConnections, useTestConnection, useDeleteConnection } from "@/queries/connections";
import { useConnectionTemplates } from "@/queries/connection-templates";
import { useAlertStore } from "@/stores/alerts-store";
import { useConnectionPrefsStore } from "@/stores/connection-prefs-store";
import { connectionBadgeClass, connectionBadgeLabel } from "../utils/connectionStatus";
import DataView from "@/components/DataView.vue";
import PageHeader from "@/components/PageHeader.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import type { ConnectionListItem } from "@/types";
import type { Config } from "datatables.net";

const router = useRouter();
const alertStore = useAlertStore();
const prefs = useConnectionPrefsStore();

const { connections, connectionsLoading: loading, connectionsError: error, refetchConnections } = useConnections();
const { templates } = useConnectionTemplates();
const { testConnection, testConnectionPending } = useTestConnection();
const { deleteConnection } = useDeleteConnection();

const searchQuery = ref("");
const selectedStatus = ref("all");
const selectedTemplate = ref("all");

// templateId → display (name + logo) so each row shows the vendor it came from.
const templateById = computed(() => {
  const map = new Map<string, { name: string; logoUrl?: string | null }>();
  for (const t of templates.value) map.set(t.id, { name: t.name, logoUrl: t.logoUrl });
  return map;
});

function templateName(c: ConnectionListItem): string {
  return templateById.value.get(c.templateId)?.name ?? c.templateId;
}
function templateLogo(c: ConnectionListItem): string | null {
  return templateById.value.get(c.templateId)?.logoUrl ?? null;
}

// Vendors the user actually has connections for — the filter only offers reachable options.
const availableTemplates = computed(() => {
  const ids = [...new Set(connections.value.map((c) => c.templateId))];
  return ids
    .map((id) => ({ id, name: templateById.value.get(id)?.name ?? id }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
});

// Filter the full list here (search spans name + vendor across every row, so it works across all
// pages — DataTables only paginates what we hand it). Sort/paging are left to DataTables.
const filteredConnections = computed(() => {
  let list = connections.value;
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    list = list.filter((c) => c.name.toLowerCase().includes(q) || templateName(c).toLowerCase().includes(q));
  }
  if (selectedStatus.value !== "all") list = list.filter((c) => connectionBadgeLabel(c) === selectedStatus.value);
  if (selectedTemplate.value !== "all") list = list.filter((c) => c.templateId === selectedTemplate.value);
  return list;
});

// Status at a glance, over the filtered set. Status is the single health axis (ADR-022).
const stats = computed(() => {
  const list = filteredConnections.value;
  return {
    total: list.length,
    connected: list.filter((c) => c.status === "Connected").length,
    requiresReauth: list.filter((c) => c.status === "RequiresReauth").length,
    setupIncomplete: list.filter((c) => c.status === "PendingSetup").length,
  };
});

const hasFilters = computed(() =>
  !!searchQuery.value || selectedStatus.value !== "all" || selectedTemplate.value !== "all");
function clearFilters() {
  searchQuery.value = "";
  selectedStatus.value = "all";
  selectedTemplate.value = "all";
}

function fmtDate(d?: string | null): string {
  return d ? new Date(d).toLocaleDateString() : "";
}

// One DataView drives both views. Search/filter is external (above); DataTables owns sort + paging,
// and `stateSave` remembers the user's sort/page. `topEnd: ""` hides DataTables' own search box —
// we filter through the page's search input instead. Cells with markup render via Vue `#slot` refs.
const tableOptions = {
  paging: true,
  pageLength: 10,
  stateSave: true,
  columns: [
    { data: "name", title: "Name", render: "#nameCell" },
    { data: "authMethodId", title: "Auth" },
    { data: "status", title: "Status", orderable: false, searchable: false, render: "#statusCell" },
    { data: null, title: "Last test", orderable: false, searchable: false, render: "#lastTestCell" },
    { data: "createdAt", title: "Created", render: (d: string) => (d ? new Date(d).toLocaleDateString() : "") },
    { data: null, title: "", orderable: false, searchable: false, className: "text-end", render: "#actionsCell" },
  ],
  layout: { topEnd: "" },
} as Config;

function openConnection(id: string) {
  router.push(`/connections/${id}`);
}
function createNew() {
  router.push("/connections/new");
}

function runTest(c: ConnectionListItem) {
  testConnection(c.id, {
    onSuccess: (result) => {
      if (result.success) alertStore.addSuccess("Test passed", `${c.name} connected successfully.`);
      else alertStore.addError("Test failed", result.message ?? `${c.name} could not connect.`);
    },
    onError: (e) => alertStore.addError("Test failed", e.userMessage),
  });
}

function remove(c: ConnectionListItem) {
  if (!confirm(`Delete "${c.name}"? It will be removed; workflows referencing it will fail.`)) return;
  deleteConnection(c.id, {
    onSuccess: () => { alertStore.addSuccess("Deleted", `${c.name} has been deleted.`); refetchConnections(); },
    onError: (e) => alertStore.addError("Delete failed", e.userMessage),
  });
}
</script>

<template>
  <main>
    <PanelGrid :columns="1">
      <PanelCard>
        <PageHeader
          title="Connections"
          :breadcrumbs="[{ label: 'Dashboard', href: '/' }, { label: 'Connections' }]"
        >
          <template #actions>
            <button class="bb-btn-primary-cta" @click="createNew" data-cy="new-connection">
              <i class="bi bi-plus"></i> New Connection
            </button>
          </template>
        </PageHeader>

        <SkeletonLoader v-if="loading || error" type="grid" :cards="6" :grid-columns="3" :error="error" @retry="refetchConnections" />

        <template v-else>
          <!-- Status at a glance -->
          <div class="row g-3 mb-4">
            <div class="col-6 col-md-3">
              <div class="card text-center"><div class="card-body">
                <div class="fs-3 fw-bold mb-2">{{ stats.total }}</div>
                <div class="text-muted small">Total</div>
              </div></div>
            </div>
            <div class="col-6 col-md-3">
              <div class="card text-center"><div class="card-body">
                <div class="fs-3 fw-bold text-success mb-2">{{ stats.connected }}</div>
                <div class="text-muted small">Connected</div>
              </div></div>
            </div>
            <div class="col-6 col-md-3">
              <div class="card text-center"><div class="card-body">
                <div class="fs-3 fw-bold text-danger mb-2">{{ stats.requiresReauth }}</div>
                <div class="text-muted small">Reconnect needed</div>
              </div></div>
            </div>
            <div class="col-6 col-md-3">
              <div class="card text-center"><div class="card-body">
                <div class="fs-3 fw-bold text-warning mb-2">{{ stats.setupIncomplete }}</div>
                <div class="text-muted small">Setup incomplete</div>
              </div></div>
            </div>
          </div>

          <!-- Search + dropdown filters + view toggle (all client-side; search spans every row). -->
          <div class="mb-4">
            <div class="row g-3">
              <div class="col-md-4">
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-search"></i></span>
                  <input type="text" class="form-control" placeholder="Search connections..." v-model="searchQuery" data-cy="search-connections" />
                </div>
              </div>
              <div class="col-md-3">
                <select class="form-select" v-model="selectedStatus" data-cy="filter-status">
                  <option value="all">All statuses</option>
                  <option value="Connected">Connected</option>
                  <option value="Reconnect needed">Reconnect needed</option>
                  <option value="Setup incomplete">Setup incomplete</option>
                </select>
              </div>
              <div class="col-md-3">
                <select class="form-select" v-model="selectedTemplate" data-cy="filter-template">
                  <option value="all">All vendors</option>
                  <option v-for="t in availableTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
                </select>
              </div>
              <div class="col-md-2">
                <div class="d-flex gap-2" role="group" aria-label="View mode">
                  <button type="button" class="bb-btn-secondary-cta flex-fill text-nowrap" :class="{ active: prefs.viewMode === 'grid' }" @click="prefs.setViewMode('grid')">
                    <i class="bi bi-grid me-1"></i>Grid
                  </button>
                  <button type="button" class="bb-btn-secondary-cta flex-fill text-nowrap" :class="{ active: prefs.viewMode === 'table' }" @click="prefs.setViewMode('table')">
                    <i class="bi bi-list me-1"></i>List
                  </button>
                </div>
              </div>
            </div>
            <div v-if="hasFilters" class="mt-2">
              <button class="bb-btn-secondary-cta btn-sm" @click="clearFilters"><i class="bi bi-x me-1"></i>Clear filters</button>
            </div>
          </div>

          <DataView
            table-id="connections-table"
            :data="filteredConnections"
            :view-mode="prefs.viewMode"
            :options="tableOptions"
            @row-click="(c: ConnectionListItem) => openConnection(c.id)"
          >
            <!-- Grid card -->
            <template #grid-card="{ row: c }">
              <div class="card h-100 card-hover" :data-cy="`connection-card-${c.id}`">
                <div class="card-body d-flex flex-column">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <h2 class="card-title mb-0 h5 d-flex align-items-center gap-2">
                      <img v-if="templateLogo(c)" :src="templateLogo(c)!" alt="" width="24" height="24" />
                      <i v-else class="bi bi-link-45deg fs-4 text-primary"></i>
                      {{ c.name }}
                    </h2>
                    <span class="badge" :class="connectionBadgeClass(c)">{{ connectionBadgeLabel(c) }}</span>
                  </div>
                  <p class="card-text text-muted small mb-1">{{ templateName(c) }} · {{ c.authMethodId }}</p>
                  <div class="mt-auto text-muted small">
                    <span v-if="c.lastTestResult">
                      Last test:
                      <span :class="c.lastTestResult.success ? 'text-success' : 'text-danger'">
                        {{ c.lastTestResult.success ? "passed" : "failed" }}
                      </span>
                    </span>
                    <span v-else>Never tested</span>
                  </div>
                </div>
                <div class="card-footer bg-transparent border-top-0 pt-0 pb-3">
                  <div class="d-flex justify-content-end gap-2">
                    <button
                      class="bb-btn-secondary-cta btn-sm"
                      :disabled="testConnectionPending || c.status !== 'Connected'"
                      title="Test connection"
                      @click.stop="runTest(c)"
                    >
                      <i class="bi bi-plug me-1"></i> Test
                    </button>
                    <div class="dropdown">
                      <button type="button" class="bb-btn-secondary-cta btn-sm dropdown-toggle" title="More actions" data-bs-toggle="dropdown" @click.stop>
                        <i class="bi bi-three-dots"></i>
                      </button>
                      <ul class="dropdown-menu dropdown-menu-end">
                        <li><a class="dropdown-item" href="#" @click.stop.prevent="openConnection(c.id)"><i class="bi bi-eye me-2"></i>View</a></li>
                        <li><a class="dropdown-item" href="#" @click.stop.prevent="router.push(`/connections/${c.id}/edit`)"><i class="bi bi-pencil me-2"></i>Edit</a></li>
                        <li><hr class="dropdown-divider" /></li>
                        <li><a class="dropdown-item text-danger" href="#" @click.stop.prevent="remove(c)"><i class="bi bi-trash me-2"></i>Delete</a></li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- List cells -->
            <template #nameCell="{ rowData: c }">
              <div class="d-flex align-items-center gap-2">
                <img v-if="templateLogo(c)" :src="templateLogo(c)!" alt="" width="18" height="18" />
                <div>
                  <div>{{ c.name }}</div>
                  <div class="small text-muted">{{ templateName(c) }}</div>
                </div>
              </div>
            </template>
            <template #statusCell="{ rowData: c }">
              <span class="badge" :class="connectionBadgeClass(c)">{{ connectionBadgeLabel(c) }}</span>
            </template>
            <template #lastTestCell="{ rowData: c }">
              <span v-if="c.lastTestResult" class="small" :class="c.lastTestResult.success ? 'text-success' : 'text-danger'">
                {{ c.lastTestResult.success ? "passed" : "failed" }} · {{ fmtDate(c.lastTestResult.testedAt) }}
              </span>
              <span v-else class="small text-muted">Never</span>
            </template>
            <template #actionsCell="{ rowData: c }">
              <!-- View is the row-click; Test/Edit/Delete are explicit (labelled per design guide). -->
              <div class="d-flex gap-2 justify-content-end">
                <button class="bb-btn-secondary-cta btn-sm text-nowrap" :disabled="testConnectionPending || c.status !== 'Connected'" @click.stop="runTest(c)"><i class="bi bi-plug me-1"></i>Test</button>
                <button class="bb-btn-secondary-cta btn-sm text-nowrap" @click.stop="router.push(`/connections/${c.id}/edit`)"><i class="bi bi-pencil me-1"></i>Edit</button>
                <button class="bb-btn-danger btn-sm text-nowrap" @click.stop="remove(c)"><i class="bi bi-trash me-1"></i>Delete</button>
              </div>
            </template>

            <template #empty-state>
              <div class="text-center py-5">
                <i class="bi bi-link-45deg display-1 text-muted"></i>
                <h2 class="mt-3 text-muted">{{ hasFilters ? "No matching connections" : "No connections yet" }}</h2>
                <p class="text-muted">{{ hasFilters ? "Try adjusting your filters or search." : "Connect a third-party service to use it in your workflows." }}</p>
                <button v-if="!hasFilters" class="bb-btn-primary-cta" @click="createNew"><i class="bi bi-plus"></i> New Connection</button>
                <button v-else class="bb-btn-secondary-cta" @click="clearFilters"><i class="bi bi-x me-1"></i>Clear filters</button>
              </div>
            </template>
          </DataView>
        </template>
      </PanelCard>
    </PanelGrid>
  </main>
</template>
